import { useEffect, useMemo, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { Card, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useHreflang } from "../../i18n/hooks/useHreflang";
import { getLocaleFromPath, localePath } from "../../i18n/config";
import { metadataTranslations } from "../../i18n/metadata";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import Button from "../../components/button/Button";
import HomeCarousel from "../../components/carousel/Carousel";
import { VideoContainer } from "../../components/layout/containers/Container";
import VCAProduct from "../../components/VCAProduct/VCAProduct";
import { getAlertsRequest } from "../../redux/actions";
import { AppState } from "../../redux/types";
import "./home.css";

const styles = {
    customBody: {
        // backgroundColor: 'rgb(247 249 248)',
        border: "none",
    },
};

const Home = () => {
    const hreflang = useHreflang("home");
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const locale = getLocaleFromPath(pathname);

    const paths = useMemo(
        () => ({
            senioritabletti: localePath("senioritabletti"),
            senioripuhelin: localePath("senioripuhelin"),
            turvaranneke: localePath("turvaranneke"),
            videopuhelu_appi: localePath("videopuhelu_appi"),
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [locale],
    );

    const localeMedia = useMemo(
        () => ({
            cover: locale === "en" ? "/assets/images/en/homepage-cover.jpg" : locale === "sv" ? "/assets/images/sv/homepage-cover.jpg" : "/assets/images/home/digihappy_main.png",
            below: locale === "en" ? "/assets/images/en/homepage-below.jpg" : locale === "sv" ? "/assets/images/sv/homepage-below.jpg" : "/assets/images/home/peli-senoritabletilla.webp",
            youtube: locale === "sv" ? "ccuOfp_YV9A" : locale === "en" ? "dLWx9rBQdwI" : "ypocJ9taIk0",
        }),
        [locale],
    );

    const currentDate = Date.now();

    useEffect(() => {
        dispatch(getAlertsRequest());
        return () => {};
    }, [dispatch]);

    const resources = useSelector((state: AppState) => state.alerts);
    const { list } = resources;

    const [video, setVideo] = useState(false);

    const meta = metadataTranslations.home[locale] || metadataTranslations.home.fi;
    let title = meta.title;
    let description = meta.description;
    if (window.location.host.split(".")[0] === "www") {
        title += " | WWW versio";
        description += " - WWW versio.";
    } else {
        description += ".";
    }

    const products = [
        {
            title: "SENIORITABLETTI",
            image: "/assets/icons/tablet.png",
            description: "Helpoin videoyhteys seniorille ja perheelle. Videopuhelut avautuvat myös itsestään. Kivaa tekemistä yksilöllisesti.",
            items: [
                "Muokattu Samsung-tabletti ja kotinäkymä",
                "Liittymä, jalusta ",
                "Videopuhelut myös ilman kosketusta Digihappy apilla",
                "Turvallinen perhetili videopuheluihin",
                "Halutut senioriystävälliset apit, kuten äänikirjat, uutiset, pasianssi",
                "Kaikki valmiina palveluna ja jatkuva it-tuki!",
            ],
            link: paths.senioritabletti,
            price: "Alk. 41e/kk",
            flags: ["FI", "SE", "GB", "IE"],
        },
        {
            title: "SENIORIPUHELIN",
            image: "/assets/icons/mobile.png",
            description: "Paras älypuhelin seniorille. Kaikki tarpeellinen, ei turhaa.",
            items: [
                "Muokattu Samsung-puhelin ja kotinäkymä ",
                "Liittymä, jalusta",
                "Videopuhelut myös ilman kosketusta Digihappy apilla",
                "Turvallinen perhetili videopuheluihin",
                "Halutut toiminnot, normaalit puhelut, tekstiviestit, apit",
                "Kaikki valmiina palveluna ja jatkuva it-tuki! ",
            ],
            link: paths.senioripuhelin,
            price: "154e + 13e/kk",
            flags: ["FI"],
        },
        {
            title: "TURVARANNEKE",
            image: "/assets/icons/watch.png",
            description: "Turvaranneke, turvakello ja kaulamalli",
            items: ["Turvaranneke tai kaulamalli", "Liittymä. Toimii ilman kännykkää.", "Paikantava turvaranneke ja turvapuhelin.", "Hälytys seniorin poistuessa alueelta tai kaatuessa", "Kaikki valmiina palveluna ja it-tuki!"],
            link: paths.turvaranneke,
            price: "Alk. 130e/215e + 19e/kk",
            flags: ["FI"],
        },
    ];

    const productData_VCA = {
        title: "SENIORIVIDEOPUHELU APPI",
        subtitle: "Helpot ja turvalliset videopuhelut seniorin ja läheisten OMILLE laitteille.",
        features: [
            "Helppo vastata – videopuhelu voi avautua ilman kosketusta",
            "Helppo soittaa – painamalla läheisen isoa kuvaa",
            "Läheisille Android - ja Apple- laitteisiin",
            "Seniorille suosittelemme Android- laitteisiin",
            "Turvallinen perhetili – vain tilin jäsenet voivat soittaa",
            "Kokeile maksutta 14 päivää",
            "9,50 e/kk, toimii kaikkialla! 🌍",
        ],
        ctaText: "Onko teillä seniorille Android -tabletti tai puhelin? Kokeile videopuheluita 14 päivää maksutta!",
        image: "/assets/images/home/app_only.png",
        link: paths.videopuhelu_appi,
    };

    const carouselItems = [
        {
            title: "Mitä asiakkaamme sanovat?",
            content: '"Mummu on aina ilahtunut videopuheluista ja hänen ei ole tarvinnut osata tehdä mitään"',
            author: "Tytär, joka asuu Ruotsissa.",
        },
        {
            title: "Mitä asiakkaamme sanovat?",
            content: '"Isäni rauhoittui, kun hän kuunteli äänikirjoja Digihappyn avulla"',
            author: "Poika, jonka isä oli ennen sairastumistaan tottunut lukemaan paljon kirjoja.",
        },
        {
            title: "Mitä asiakkaamme sanovat?",
            content: '"Digihappy helpottaa paljon, kun voimme videopuheluilla varmistaa, että isämme ottaa lääkkeensä"',
            author: "Perhe, jonka jäsenet soittavat videopuhelun vuoropäivinä yksin asuvalle 85- vuotiaalle isälleen.",
        },
        {
            title: "Mitä asiakkaamme sanovat?",
            content: '"Digihappy on tuonut meille paljon iloa. Soitin miehelleni sen avulla videopuhelun kolmesti päivässä. Olen suositellut sitä ystävillenikin!"',
            author: "85- vuotias rouva, joka soitti videopuheluita iäkkäälle miehelleen Digihappylla.",
        },
        {
            title: "Mitä asiakkaamme sanovat?",
            content: '"Olemme etsineet vuoden tällaista palvelua"',
            author: "Tytär, joka soittaa päivittäin Digihappy videopuhelun äidilleen.",
        },
        {
            title: "Mitä asiakkaamme sanovat?",
            content: '"Digihappyn palvelu on ystävällistä ja asiantuntevaa. Apua saa henkilökohtaisesti."',
            author: "Poika, jonka äidille tehtiin henkilökohtaisia asetuksia tablettiin.",
        },
        {
            title: "Mitä asiakkaamme sanovat?",
            content: '"Hankimme iäkkäälle isälleni Digihappyn alkuvuodesta - se on aivan loistava"',
            author: "Tytär, joka puhuu videopuheluja 90- vuotiaan isän kanssa.",
        },
        {
            title: "Mitä asiakkaamme sanovat?",
            content: '"Turvapuhelimen avulla minut pystyttiin tavoittamaan ja paikantamaan helposti, kun olin kaatunut."',
            author: "Mies, joka soitti turvarannekkeella kaaduttuaan.",
        },
    ];
    return (
        <>
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={description} />
                <link rel="alternate" hrefLang="fi" href={hreflang.fi} />
                <link rel="alternate" hrefLang="en" href={hreflang.en} />
                <link rel="alternate" hrefLang="sv" href={hreflang.sv} />
                <link rel="alternate" hrefLang="x-default" href={hreflang.fi} />
            </Helmet>
            {/* <TextOnImage
                height={`calc(100vh - ${
                    56 +
                    56 *
                        list?.filter((el) => {
                            // @ts-ignore
                            return (!el.start || new Date(el.start) <= currentDate) && (!el.end || new Date(el.end) >= currentDate);
                        })?.length -
                    1
                }px)`}
                width="100vw"
                text1={texts[7].text1}
                text2={texts[7].text2}
                backgroundImage={homepage}
                backgroundSize="cover"
                backgroundPosition="center top"
                backgroundRepeat="no-repeat"
                imgClassName="girlpic"
                className1="girlpic-text"
                className2="girlpic-link"
            /> */}

            <Container
                fluid
                style={{
                    height: `calc(100vh - ${
                        56 +
                        56 *
                            list?.filter((el) => {
                                // @ts-ignore
                                return (!el.start || new Date(el.start) <= currentDate) && (!el.end || new Date(el.end) >= currentDate);
                            })?.length -
                        1
                    }px)`,
                    width: "100vw",
                    backgroundRepeat: "no-repeat",
                    backgroundImage: `url('${localeMedia.cover}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "66%",
                    position: "relative",
                    minHeight: 640,
                }}
                aria-label="Nainen käyttää tablettia, jonka näytöllä näkyy yhteystietoluettelo."
                className="img-fluid contact-pic"
            >
                <div className="global-banner-text-container" style={{ maxWidth: "100%", width: "100%" }}>
                    <h3 className="global-banner-title" style={{ alignSelf: "end" }}>
                        Tuo turvaa ja iloa seniorille
                    </h3>
                    <p className="global-banner-text" style={{ alignSelf: "end" }}>
                        Helpot videopuhelut, hälytykset ja kivaa tekemistä
                    </p>
                </div>
            </Container>

            <Container className="text-container">
                <div className="header-div">
                    <h1 className="header-text first-text" style={{ textAlign: "center" }}>
                        Aidosti helppo senioritabletti, senioripuhelin ja turvaranneke. Valmiina palveluna ja jatkuva it-tuki. Videopuhelut ilman kosketusta, kivaa tekemistä, hälytykset napin painalluksella. Osta nyt!
                    </h1>
                </div>
            </Container>
            <div className="d-flex justify-content-center mb-4">
                <Link to={localePath("osta")} className="explore" style={{ textDecoration: "none" }}>
                    Osta ja tutustu hintoihin
                </Link>
            </div>
            <section className="image-rows">
                <div className="home-grid-container">
                    {products.map((product, key) => (
                        <div className="home-grid-column" key={key}>
                            <ProductCard product={product} />
                        </div>
                    ))}
                </div>
                <VCAProduct data={productData_VCA} />
            </section>
            <section className="home-carousel-container">
                <HomeCarousel items={carouselItems} />
            </section>
            <section className="image-rows">
                <Row noGutters>
                    <Col sm={12} md={12} lg={6} className="home-column">
                        <Card className="home-image-card manfamily">
                            <Card.Img src={"/assets/images/home/helpot-videopuhelut-senioripuhelimella.webp"} variant="top" height="100vh" width="auto" alt="Uusi Samsung Galaxy S10 5G ja kosketusnäyttökynä pöydällä. " />

                            <Card.Body style={styles.customBody}>
                                <Card.Title as="div" className="image-text text-center">
                                    Helppo videopuhelu puhelimessa ja tabletissa. Vastaus haluttaessa ilman kosketusta. Soitto painamalla läheisen kuvaa.
                                </Card.Title>
                                <Card.Text as="div" className="image-link text-center">
                                    <HashLink to={paths.senioripuhelin} className="lue-lisää-linkki">
                                        <strong>Lue lisää</strong>
                                    </HashLink>
                                </Card.Text>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col sm={12} md={12} lg={6} className="home-column">
                        <Card className="home-image-card woman2">
                            <Card.Img src={localeMedia.below} variant="top" height="550" width="auto" alt="Seniori pelaa korttipeliä tabletilla" />

                            <Card.Body style={styles.customBody}>
                                <Card.Title as="div" className="image-text text-center">
                                    Muokattu kotinäkymä ja ominaisuudet tabletissa ja puhelimessa. Eri vaihtoehdot seniorille, joka ei pysty koskemaan laitteeseen ja aktiiviselle seniorille.
                                </Card.Title>

                                <Card.Text as="div" className="image-link text-center">
                                    <HashLink to={paths.senioritabletti} className="lue-lisää-linkki">
                                        <strong>Lue lisää</strong>
                                    </HashLink>
                                </Card.Text>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                <section className="between-image-rows" />
                <Row noGutters>
                    <Col sm={12} md={12} lg={6} className="home-column">
                        <Card className="home-image-card it-woman">
                            <div className="global-credit-image-container">
                                <Card.Img src={"/assets/images/home/paras-turvaranneke.webp"} variant="top" height="550" width="auto" alt="Henkilö pitelee ovenkahvaa, ranteessaan musta kello." />
                                <span className="global-credit-text">BILD: : Björn Johansson/Posifon</span>
                            </div>
                            <Card.Body style={styles.customBody}>
                                <Card.Title as="div" className="image-text text-center">
                                    Napin painalluksella hälytys ja puheyhteys läheisiin. Gps-paikannus, joka ei ole sidottu seniorin kännykkään. Alue-kaatumis- ja akku loppuu hälytys.
                                </Card.Title>
                                <Card.Text as="div" className="image-link text-center">
                                    <HashLink to={paths.turvaranneke} className="lue-lisää-linkki">
                                        <strong>Lue lisää</strong>
                                    </HashLink>
                                </Card.Text>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col sm={12} md={12} lg={6} className="home-column">
                        <Card className="home-image-card doctor1">
                            <Card.Img src={"/assets/images/home/4.jpg"} variant="top" height="550" width="auto" />

                            <Card.Body style={styles.customBody}>
                                <Card.Title as="div" className="image-text text-center">
                                    Palveluna valmiit asetukset, yhteystiedot, kotinäkymä, sovellukset ja liittymä. Alkuneuvonta, jatkuva it-tuki, etähallinta ja tietoturva.
                                </Card.Title>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                <section className="between-image-rows" />
            </section>

            {/*<TextOnImage*/}
            {/*    text1={texts[0].text1}*/}
            {/*    // text2={texts[0].text2}*/}
            {/*    text3={texts[0].text3}*/}
            {/*    link={texts[0].link}*/}
            {/*    height='120vh'*/}
            {/*    width='100vw'*/}
            {/*    backgroundImage={*/}
            {/*      'https://senioritabletti-image-bucket.s3.eu-north-1.amazonaws.com/bottompic.jpg'*/}
            {/*    }*/}
            {/*    backgroundSize='cover'*/}
            {/*    backgroundPosition='center center'*/}
            {/*    backgroundRepeat='no-repeat'*/}
            {/*    backgroundAttachment=''*/}
            {/*    imgClassName='bottompic'*/}
            {/*    className1='bottompic-text'*/}
            {/*    className2='bottompic-link'*/}
            {/*    className3='bottom-link'*/}
            {/*/>*/}

            <section className="image-rows" style={{ marginBottom: 48 }}>
                <Row className="justify-content-md-center">
                    <Col sm={12} md={12} lg={8} className="home-column">
                        <Card className="home-image-card doctor1">
                            <Card.Body style={styles.customBody}>
                                <Card.Title
                                    as="div"
                                    className="image-text text-center"
                                    style={{
                                        marginBottom: "1.6rem",
                                    }}
                                >
                                    <Button
                                        onClick={() => {
                                            if (!video) {
                                                setVideo(true);
                                            }
                                        }}
                                    >
                                        Katso video
                                    </Button>
                                </Card.Title>
                                {localeMedia.youtube ? (
                                    <VideoContainer>
                                        {video && (
                                            <iframe
                                                src={`https://www.youtube.com/embed/${localeMedia.youtube}?autoplay=1&rel=0&color=white&modestbranding=1`}
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                                title="Digihappy Videon"
                                                className="global-youtube-video"
                                            />
                                        )}
                                        <div className="global-video-overlay" style={{ opacity: video ? 0 : 1, pointerEvents: video ? "none" : "auto" }}>
                                            <img
                                                id="video-cover"
                                                src={"/assets/images/home/digihappy videon pikkukuva.png"}
                                                alt="Digihappy Videon Pikkukuva"
                                                style={{
                                                    height: "auto",
                                                    width: "100%",
                                                }}
                                            />
                                            <button
                                                id="play"
                                                className="play-btn"
                                                onClick={() => {
                                                    setVideo(true);
                                                }}
                                            >
                                                <i
                                                    className="fas fa-play global-hover-zoom"
                                                    style={{
                                                        color: "#ffffff",
                                                        fontSize: "min(6rem, 15vw)",
                                                    }}
                                                />
                                            </button>
                                        </div>
                                    </VideoContainer>
                                ) : (
                                    <div style={{ background: "#e0e0e0", width: "100%", aspectRatio: "16/9" }} />
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </section>
        </>
    );
};

const ProductCard = ({ product }: { product: { title: string; image: string; description: string; items: string[]; price: string; flags: string[]; link: string } }) => {
    return (
        <Link to={product.link}>
            <Card className="home-card-2024">
                <Card.Title className="image-text text-center home-card-title">{product.title}</Card.Title>
                <img src={product.image} alt="T" className="home-card-image" />
                <Card.Text className="image-text text-center home-card-text home-card-description">{product.description}</Card.Text>
                <Card.Text as="div" className="image-text text-center home-card-text home-card-list">
                    {product.items.map((item, key) => (
                        <div className="home-card-list-item" key={key}>
                            <i className="fas fa-check-circle" />
                            <span className="image-text home-card-text">{item}</span>
                        </div>
                    ))}
                    <div className="home-card-list-item home-card-price-row">
                        <i className="fas fa-check-circle" />
                        <span className="image-text home-card-text home-card-price">{product.price}</span>
                        <span className="home-card-flags">
                            {product.flags.map((code, key) => (
                                <ReactCountryFlag key={key} countryCode={code} svg className="home-card-flag" title={code} />
                            ))}
                        </span>
                    </div>
                </Card.Text>
            </Card>
        </Link>
    );
};

export default Home;
